// Màn hình "Bảo hành" — KPI bảo hành đối tác từ ops-quality: SL phiếu vào KPI +
// ngày xử lý TB + % KPI (snapshot Worker), phiếu đang chờ về (aging), yêu cầu
// gỡ KPI, trend theo tháng KPI. View-only.
import { KpiCard } from '../components/KpiCard';
import { Tag } from '../components/Tag';
import { ChartBox, chartDataset } from '../components/ChartBox';
import type { OpsWarrantyResponse } from '../types';
import { asNumber, monthLabel, num, pct1, uniq } from '../utils';

interface OpsWarrantyScreenProps {
  data: OpsWarrantyResponse | null;
  loaded: boolean;
  busy: boolean;
  month: string;
  onMonth: (month: string) => void;
}

const kpiTone = (v: number): 'green' | 'blue' | 'red' => (v >= 110 ? 'green' : v >= 100 ? 'blue' : 'red');
const ageTone = (d: number): 'red' | 'amber' | 'gray' => (d > 10 ? 'red' : d > 7 ? 'amber' : 'gray');

export function OpsWarrantyScreen({ data, loaded, busy, month, onMonth }: OpsWarrantyScreenProps) {
  if (busy && !loaded) return <div className="card empty">Đang tải dữ liệu bảo hành...</div>;
  if (!loaded) return <div className="card empty">Mở mục Bảo hành để tải dữ liệu.</div>;

  const months = data?.meta?.months || [];
  const stats = data?.stats || [];
  const kpi = data?.kpi || [];
  const targets = data?.targets || [];
  const e2 = data?.e2 || [];
  const waiting = data?.waiting || [];
  const exclusions = data?.exclusions || [];
  const trend = data?.trend || [];
  const aging = data?.aging || [];

  const slTotal = stats.reduce((a, r) => a + asNumber(r.sl), 0);
  const sumDays = stats.reduce((a, r) => a + asNumber(r.avg_days) * asNumber(r.sl), 0);
  const avgAll = slTotal ? Math.round((sumDays / slTotal) * 100) / 100 : 0;
  const waitTotal = waiting.reduce((a, r) => a + asNumber(r.cnt), 0);
  const over10Total = waiting.reduce((a, r) => a + asNumber(r.over10), 0);
  const e2Total = e2.reduce((a, r) => a + asNumber(r.cnt), 0);
  const exclApproved = exclusions.filter(r => r.review_status === 'approved').reduce((a, r) => a + asNumber(r.cnt), 0);
  const exclPending = exclusions.filter(r => r.review_status === 'pending').reduce((a, r) => a + asNumber(r.cnt), 0);

  const kpiMap = new Map(kpi.map(r => [r.store_code, r]));
  const targetMap = new Map(targets.map(r => [r.store_code, asNumber(r.target_value)]));
  const e2Map = new Map(e2.map(r => [r.store_code, asNumber(r.cnt)]));
  const waitMap = new Map(waiting.map(r => [r.store_code, r]));
  const exclByStore = (code: string, status: string) =>
    exclusions.filter(r => r.store_code === code && r.review_status === status).reduce((a, r) => a + asNumber(r.cnt), 0);

  // Trend: SL cột (stack theo store) + ngày TB toàn hệ thống (line).
  const trendMonths = uniq(trend.map(r => r.kpi_month));
  const trendStores = uniq(trend.map(r => r.store_code));
  const STORE_COLORS: Record<string, string> = { NCV: '#2563eb', HHT: '#d97706', KVC: '#059669', TBT: '#7c3aed', TH: '#0891b2' };
  const slSets = trendStores.map(code => {
    const byYm = new Map(trend.filter(r => r.store_code === code).map(r => [r.kpi_month, asNumber(r.sl)]));
    const ds = chartDataset(code, trendMonths.map(m => byYm.get(m) ?? 0), STORE_COLORS[code] || '#6b7280');
    return { ...ds, stack: 'sl' } as typeof ds;
  });
  const avgLine = trendMonths.map(m => {
    const rows = trend.filter(r => r.kpi_month === m);
    const sl = rows.reduce((a, r) => a + asNumber(r.sl), 0);
    const days = rows.reduce((a, r) => a + asNumber(r.avg_days) * asNumber(r.sl), 0);
    return sl ? Math.round((days / sl) * 100) / 100 : NaN;
  });
  const trendSets = [...slSets, chartDataset('Ngày TB', avgLine, '#d71920', 'line', 'y1')];

  return (
    <>
      <section className="filters">
        <div className="actions start">
          <span className="kpi-label">Tháng KPI</span>
          {months.map(m => (
            <button key={m} className={`tab ${month === m ? 'active' : ''}`} onClick={() => onMonth(m)}>{monthLabel(m)}</button>
          ))}
        </div>
      </section>

      <section className="grid kpis">
        <KpiCard label="Phiếu vào KPI" value={num(slTotal)} sub={`${monthLabel(month)} · đã loại gỡ KPI duyệt`} tone="blue" />
        <KpiCard label="Ngày xử lý TB" value={`${avgAll} ngày`} sub="phiếu đã về (C19) − ngày tạo" tone={avgAll <= 11 ? 'green' : 'red'} />
        <KpiCard label="Đang chờ về" value={num(waitTotal)} sub={`${num(over10Total)} phiếu quá 10 ngày`} tone={over10Total ? 'red' : 'amber'} />
        <KpiCard label="Loại E2 (<15')" value={num(e2Total)} sub="xử lý nhanh, không tính KPI" tone="gray" />
        <KpiCard label="Gỡ KPI" value={num(exclApproved)} sub={`đã duyệt · ${num(exclPending)} chờ duyệt`} tone={exclPending ? 'amber' : 'green'} />
      </section>

      <section className="card space">
        <div className="section-title">
          <h2>KPI bảo hành theo cửa hàng · {monthLabel(month)}</h2>
          <span>% KPI theo snapshot ops-quality {data?.meta?.snapshot_date ? `(${data.meta.snapshot_date})` : ''} · target ngày theo tháng</span>
        </div>
        <div className="scroll">
          <table>
            <thead><tr>
              <th>Cửa hàng</th><th className="right">SL phiếu</th><th className="right">Ngày TB</th><th className="right">Target</th>
              <th className="right">% KPI</th><th className="right">Chờ về</th><th className="right">Tuổi TB chờ</th>
              <th className="right">Quá 10 ngày</th><th className="right">E2</th><th className="right">Gỡ KPI (duyệt/chờ)</th>
            </tr></thead>
            <tbody>{stats.map(r => {
              const k = kpiMap.get(r.store_code);
              const w = waitMap.get(r.store_code);
              const target = targetMap.get(r.store_code);
              const pct = k ? asNumber(k.kpi_pct) : null;
              return (
                <tr key={r.store_code}>
                  <td><b>{r.store_code}</b></td>
                  <td className="right tnum"><b>{num(r.sl)}</b></td>
                  <td className="right tnum">{num(asNumber(r.avg_days))}</td>
                  <td className="right tnum muted">{target != null ? `${target} ngày` : '-'}</td>
                  <td className="right">{pct == null ? <span className="muted">-</span> : <Tag tone={kpiTone(pct)}>{pct1(pct)}</Tag>}</td>
                  <td className="right tnum">{w ? num(w.cnt) : <span className="muted">0</span>}</td>
                  <td className="right tnum">{w ? `${num(asNumber(w.avg_age))} ngày` : <span className="muted">-</span>}</td>
                  <td className="right tnum">{w && asNumber(w.over10) ? <Tag tone="red">{num(w.over10)}</Tag> : <span className="muted">0</span>}</td>
                  <td className="right tnum muted">{num(e2Map.get(r.store_code) || 0)}</td>
                  <td className="right tnum">{num(exclByStore(r.store_code, 'approved'))} / {num(exclByStore(r.store_code, 'pending'))}</td>
                </tr>
              );
            })}
            {stats.length === 0 && <tr><td colSpan={10} className="empty">Chưa có phiếu KPI cho {monthLabel(month)}.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <section className="grid split atop space">
        <div className="card">
          <div className="section-title">
            <h2>Trend theo tháng KPI</h2>
            <span>Cột = SL phiếu theo cửa hàng · đường = ngày xử lý TB toàn hệ thống</span>
          </div>
          <div className="chart"><ChartBox labels={trendMonths.map(monthLabel)} datasets={trendSets} options={{ scales: { x: { stacked: true, grid: { display: false } }, y: { stacked: true, beginAtZero: true }, y1: { position: 'right', beginAtZero: true, grid: { display: false } } } }} /></div>
        </div>
        <div className="card">
          <div className="section-title">
            <h2>Phiếu chờ về lâu nhất</h2>
            <span>{num(aging.length)} phiếu chưa có C19 · quá 10 ngày cần đòi hãng gấp</span>
          </div>
          <div className="scroll">
            <table>
              <thead><tr>
                <th>Mã phiếu</th><th>Kho</th><th>Sản phẩm</th><th>Trạng thái</th><th className="right">Tuổi</th>
              </tr></thead>
              <tbody>{aging.map(r => {
                const age = asNumber(r.age_days);
                return (
                  <tr key={r.oid}>
                    <td className="tnum">{r.oid}<div className="muted">{r.created_day}</div></td>
                    <td><b>{r.store_code}</b></td>
                    <td className="need">{r.product || <span className="muted">-</span>}</td>
                    <td className="muted">{r.status_name || '-'}</td>
                    <td className="right"><Tag tone={ageTone(age)}>{num(Math.round(age))} ngày</Tag></td>
                  </tr>
                );
              })}
              {aging.length === 0 && <tr><td colSpan={5} className="empty">Không có phiếu nào đang chờ về.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </>
  );
}
